import React, { useEffect, useRef, useState } from 'react';

export default function ScrollReveal({
  children,
  direction = 'up',
  delay = 0,
  duration = 800,
  className = '',
  threshold = 0.1,
}) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          // Once it intersects, we can stop observing to keep the element visible
          if (ref.current) {
            observer.unobserve(ref.current);
          }
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px', // Trigger slightly before it enters the viewport fully
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  const getDirectionStyles = () => {
    switch (direction) {
      case 'up':
        return isIntersecting ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12';
      case 'down':
        return isIntersecting ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-12';
      case 'left':
        return isIntersecting ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12';
      case 'right':
        return isIntersecting ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12';
      case 'fade':
        return isIntersecting ? 'opacity-100' : 'opacity-0';
      default:
        return isIntersecting ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12';
    }
  };

  return (
    <div
      ref={ref}
      className={`transition-all ease-out ${getDirectionStyles()} ${className}`}
      style={{
        transitionDuration: `${duration}ms`,
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}
